import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Method,
  Watch,
  Event,
  EventEmitter
} from "@stencil/core";

import {
  StzhAccordionItemOpenEvent,
  StzhAccordionItemOpenedEvent,
  StzhAccordionItemCloseEvent,
  StzhAccordionItemClosedEvent
} from "../../index";

import { isStzhElement } from "../../utils/utils";
import { waitForEvent } from "../../utils/event-utils";
import { stopAnimations, animateTo, shimKeyframesAutoValues } from "../../utils/animation-utils";

const ANIMATION_SHOW = {
  keyframes: [
    { height: "0", opacity: "0" },
    { height: "auto", opacity: "1" }
  ],
  options: { duration: 300, easing: "linear" }
};

const ANIMATION_HIDE = {
  keyframes: [
    { height: "auto", opacity: "1" },
    { height: "0", opacity: "0" }
  ],
  options: { duration: 300, easing: "linear" }
};

let detailsCounter = 0;

/**
 * @slot - Slot for trigger
 * @slot content - Slot for any content
 */
@Component({
  tag: "stzh-details",
  styleUrl: "stzh-details.scss",
  scoped: true
})
export class StzhDetails {
  /** Whether fold is open */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  // /** Whether to remove top padding */
  // @Prop({ reflect: true }) noPaddingTop: boolean = false;

  // /** Whether to remove top padding */
  // @Prop({ reflect: true }) noPaddingBottom: boolean = false;

  @Element() element: HTMLStzhDetailsElement;

  /** Open event */
  @Event() stzhOpen: EventEmitter<StzhAccordionItemOpenEvent>;

  /** Opened event (after animation) */
  @Event() stzhOpened: EventEmitter<StzhAccordionItemOpenedEvent>;

  /** Close event */
  @Event() stzhClose: EventEmitter<StzhAccordionItemCloseEvent>;

  /** Closed event (after animation) */
  @Event() stzhClosed: EventEmitter<StzhAccordionItemClosedEvent>;

  @Watch("open")
  async openWatcher(newValue: boolean) {
    if (!this.triggerElement) {
      return;
    }

    if (this.triggerElement.firstElementChild) {
      const isStzh = isStzhElement(this.triggerElement.firstElementChild);

      this.triggerElement.firstElementChild
        .setAttribute(isStzh ? "a11y-expanded" : "aria-expanded", newValue ? "true" : "false");
    }

    if (newValue) {
      if (this.toggledByMethod) {
        this.stzhOpen.emit({
          component: "stzh-accordion-item"
        });
      }

      await stopAnimations(this.bodyElement);
      this.bodyElement.hidden = false;

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_SHOW.keyframes, {
          height: this.bodyElement.scrollHeight
        }),
        ANIMATION_SHOW.options
      );

      this.bodyElement.style.height = "auto";
      this.bodyElement.style.overflow = "visible";

      if (this.toggledByMethod) {
        this.stzhOpened.emit({
          component: "stzh-accordion-item"
        });
      }
    } else {
      if (this.toggledByMethod) {
        this.stzhClose.emit({
          component: "stzh-accordion-item"
        });
      }

      await stopAnimations(this.bodyElement);
      this.bodyElement.style.overflow = "hidden";

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_HIDE.keyframes, {
          height: this.bodyElement.scrollHeight
        }),
        ANIMATION_HIDE.options
      );

      this.bodyElement.hidden = true;
      this.bodyElement.style.height = "0px";

      if (this.toggledByMethod) {
        this.stzhClosed.emit({
          component: "stzh-accordion-item"
        });
      }
    }

    this.toggledByMethod = false;
  }

  private toggledByMethod: boolean = false;
  private bodyElement: HTMLDivElement;
  private detailsId: string;

  /** Show accordion item content */
  @Method()
  async show() {
    if (this.open) {
      return;
    }

    this.toggledByMethod = true;
    this.open = true;
    return waitForEvent(this.element, "stzhOpened");
  }

  /** Hide accordion item content */
  @Method()
  async hide() {
    if (!this.open) {
      return;
    }

    this.toggledByMethod = true;
    this.open = false;
    return waitForEvent(this.element, "stzhClosed");
  }

  @Method()
  async toggle() {
    if (this.open) {
      return await this.hide();
    } else {
      return await this.show();
    }
  }

  private triggerElement: HTMLElement;

  private onClickTrigger = () => {
    this.toggle();
  }

  componentWillLoad() {
    this.detailsId = `stzh-details-${detailsCounter++}`;
  }

  componentDidLoad() {
    this.bodyElement.hidden = !this.open;
    this.bodyElement.style.height = this.open ? "auto" : "0px";
    this.bodyElement.style.overflow = this.open ? "visible" : "hidden";

    if (this.triggerElement.firstElementChild) {
      const isStzh = isStzhElement(this.triggerElement.firstElementChild);
      this.triggerElement.firstElementChild.setAttribute(isStzh ? "a11y-controls" : "aria-controls", `${this.detailsId}-content`);
      this.triggerElement.firstElementChild.setAttribute(isStzh ? "a11y-expanded" : "aria-expanded", this.open ? "true" : "false");
    }
  }

  connectedCallback() {
    let trigger = this.triggerElement?.firstElementChild;

    if (!trigger) {
      const defaultSlot = Array.from(this.element.children)
        .find(child => !(child as HTMLElement).hasAttribute("slot")) as HTMLElement

      trigger = defaultSlot;
    }

    if (trigger) {
      trigger.addEventListener("click", this.onClickTrigger);
    }
  }

  disconnectedCallback() {
    if (this.triggerElement && this.triggerElement.firstElementChild) {
      this.triggerElement.firstElementChild.removeEventListener("click", this.onClickTrigger);
    }
  }

  render() {
    const classes = {
      "stzh-details": true,
      "stzh-details--is-open": this.open
    };

    return (
      <Host>
        <div class={classes}>
          <div
            ref={(el) => (this.triggerElement = el as HTMLDivElement)}
            class="stzh-details__trigger"
          >
            <slot></slot>
          </div>
          <div
            id={`${this.detailsId}-content`}
            ref={(el) => (this.bodyElement = el as HTMLDivElement)}
            class="stzh-details__content"
            aria-labelledby={`${this.detailsId}-header`}
          >
            <div class="stzh-details__content-inner">
              <slot name="content"></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
